/**
 * `<caos-stat-tile>` — a single headline number with its label and trend.
 * (Catalog: Data display › StatTile — a single headline number with its label and trend.)
 *
 * A `label` (mono eyebrow), a big `value` (display face, tabular figures), and an
 * optional `delta` coloured by `trend` (up = success, down = danger, flat = muted).
 * An optional `intent` (info/success/warning/danger) tints the whole tile with the
 * SEMANTIC `-bg` fill; on that tint the label/value read the theme-FIXED intent INK
 * tokens (`-text` / `-text-muted`), never the theme-flipping neutral text tokens —
 * so the tile is legible in both themes and stays semantic under a brand override
 * (AF2 contrast lesson). A `visual` slot holds an optional sparkline.
 *
 * A `clickable` attribute makes the tile a deep-link affordance (Dragon's app-
 * overview KPI cards navigate): the tile shows a pointer, its border warms to the
 * accent on hover/focus, it is keyboard-focusable, and Enter/Space/click emit a
 * bubbling `caos-stat-select` event (`detail: { label, value }`).
 *
 * Styling seams: `::part(tile)`, `::part(label)`, `::part(value)`, `::part(delta)`,
 * `::part(visual)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export type CaosStatIntent = 'info' | 'success' | 'warning' | 'danger';
export type CaosStatTrend = 'up' | 'down' | 'flat';

const sheet = makeSheet(`
  :host { display: block; }

  .tile {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
    padding: var(--caos-space-4);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
    min-width: 0;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
  }

  /* clickable — a KPI deep-link affordance (Dragon's app-home cards navigate) */
  :host([clickable]) .tile { cursor: pointer; }
  :host([clickable]) .tile:hover { border-color: var(--caos-color-accent); }
  :host([clickable]) .tile:focus-visible {
    outline: none;
    border-color: var(--caos-color-accent);
    box-shadow: 0 0 0 3px var(--caos-accent-wash);
  }

  /* intent tint — semantic fill + semantic INK (both theme-fixed, brand-decoupled) */
  :host([intent="info"]) .tile {
    background: var(--caos-color-info-bg);
    border-color: color-mix(in srgb, var(--caos-color-info) 35%, transparent);
  }
  :host([intent="success"]) .tile {
    background: var(--caos-color-success-bg);
    border-color: color-mix(in srgb, var(--caos-color-success) 35%, transparent);
  }
  :host([intent="warning"]) .tile {
    background: var(--caos-color-warning-bg);
    border-color: color-mix(in srgb, var(--caos-color-warning) 35%, transparent);
  }
  :host([intent="danger"]) .tile {
    background: var(--caos-color-danger-bg);
    border-color: color-mix(in srgb, var(--caos-color-danger) 35%, transparent);
  }

  .label {
    font-family: var(--caos-font-mono);
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
    letter-spacing: var(--caos-tracking-wide);
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }
  /* on a tint, the muted ink is the theme-fixed intent muted ink */
  :host([intent="info"]) .label { color: var(--caos-color-info-text-muted); }
  :host([intent="success"]) .label { color: var(--caos-color-success-text-muted); }
  :host([intent="warning"]) .label { color: var(--caos-color-warning-text-muted); }
  :host([intent="danger"]) .label { color: var(--caos-color-danger-text-muted); }

  .value {
    font-family: var(--caos-font-display);
    font-weight: 700; /* Dragon KPI value weight (off the 800 display step) */
    font-size: var(--caos-text-kpi); /* 26px app-home KPI value */
    line-height: 1.05;
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text);
    overflow-wrap: anywhere;
  }
  :host([intent="info"]) .value { color: var(--caos-color-info-text); }
  :host([intent="success"]) .value { color: var(--caos-color-success-text); }
  :host([intent="warning"]) .value { color: var(--caos-color-warning-text); }
  :host([intent="danger"]) .value { color: var(--caos-color-danger-text); }

  .footer { display: flex; align-items: center; gap: var(--caos-space-2); }

  .delta {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-1);
    font-family: var(--caos-font);
    font-size: 12px; /* sub-line */
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    /* trend inks are the theme-fixed semantic -text inks (≥4.5:1 in both themes) */
    color: var(--caos-color-text-muted);
  }
  .delta:empty { display: none; }
  .delta svg { display: block; width: 14px; height: 14px; }
  :host([trend="up"]) .delta { color: var(--caos-color-success-text); }
  :host([trend="down"]) .delta { color: var(--caos-color-danger-text); }
  :host([trend="flat"]) .delta { color: var(--caos-color-text-muted); }

  .visual { display: block; margin-left: auto; }
  .visual:not(:has(*)) { display: none; }
`);

export class CaosStatTile extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'value', 'delta', 'trend', 'clickable'];
  }

  private tileEl: HTMLDivElement;
  private labelEl: HTMLDivElement;
  private valueEl: HTMLDivElement;
  private deltaEl: HTMLSpanElement;
  private deltaText: Text;

  constructor() {
    super(sheet);

    const tile = document.createElement('div');
    tile.className = 'tile';
    tile.setAttribute('part', 'tile');
    this.tileEl = tile;
    tile.addEventListener('click', () => this.emitSelect());
    tile.addEventListener('keydown', (e) => {
      if (!this.hasAttribute('clickable')) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        this.emitSelect();
      }
    });

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');

    this.valueEl = document.createElement('div');
    this.valueEl.className = 'value';
    this.valueEl.setAttribute('part', 'value');

    const footer = document.createElement('div');
    footer.className = 'footer';

    this.deltaEl = document.createElement('span');
    this.deltaEl.className = 'delta';
    this.deltaEl.setAttribute('part', 'delta');
    this.deltaText = document.createTextNode('');
    this.deltaEl.append(this.deltaText);

    const visual = document.createElement('div');
    visual.className = 'visual';
    visual.setAttribute('part', 'visual');
    const visualSlot = document.createElement('slot');
    visualSlot.name = 'visual';
    visual.append(visualSlot);

    footer.append(this.deltaEl, visual);
    tile.append(this.labelEl, this.valueEl, footer);
    this.root.append(tile);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private emitSelect(): void {
    if (!this.hasAttribute('clickable')) return;
    this.dispatchEvent(
      new CustomEvent('caos-stat-select', {
        bubbles: true,
        composed: true,
        detail: { label: this.getAttribute('label') ?? '', value: this.getAttribute('value') ?? '' },
      }),
    );
  }

  private sync(): void {
    this.labelEl.textContent = this.getAttribute('label') ?? '';
    this.valueEl.textContent = this.getAttribute('value') ?? '';

    // clickable → focusable button affordance
    if (this.hasAttribute('clickable')) {
      this.tileEl.tabIndex = 0;
      this.tileEl.setAttribute('role', 'button');
    } else {
      this.tileEl.removeAttribute('tabindex');
      this.tileEl.removeAttribute('role');
    }

    const delta = this.getAttribute('delta') ?? '';
    const trend = this.getAttribute('trend');
    this.deltaText.textContent = delta;
    // Prepend a trend glyph when there is a delta and a direction.
    const existingIcon = this.deltaEl.querySelector('svg');
    if (existingIcon) existingIcon.remove();
    if (delta && trend) {
      const name = trend === 'down' ? 'trend-down' : trend === 'flat' ? 'trend-flat' : 'trend-up';
      this.deltaEl.insertBefore(makeIcon(name), this.deltaText);
    }
  }
}

define('caos-stat-tile', CaosStatTile);

declare global {
  interface HTMLElementTagNameMap {
    'caos-stat-tile': CaosStatTile;
  }
}
